import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ReportProblem as ReportProblemIcon,
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { ReportReason } from '../../services/reportService';

export type ReportStatus = 'submitted' | 'reviewing' | 'resolved' | 'rejected';

type UserMini = {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  alias?: string | null;
};

type ProductMini = {
  id: string;
  name?: string | null;
};

export type ProductReportAdminRow = {
  id: string;
  reporter_id: string;
  seller_id: string;
  product_id: string;
  subject: string;
  reason: ReportReason;
  details: string;
  status: ReportStatus;
  created_at: string;
  updated_at: string;
  reporter?: UserMini | null;
  seller?: UserMini | null;
  product?: ProductMini | null;
};

const reasonLabel = (r: ReportReason): string => {
  switch (r) {
    case 'prohibited_item':
      return 'Prohibited item';
    case 'counterfeit':
      return 'Counterfeit / replica';
    case 'misleading_description':
      return 'Misleading description';
    case 'fraud_scam':
      return 'Fraud / scam';
    case 'stolen_item':
      return 'Stolen item';
    case 'hate_harassment':
      return 'Hate / harassment';
    case 'adult_content':
      return 'Adult content';
    default:
      return 'Other';
  }
};

const statusMeta = (s: ReportStatus): { label: string; color: 'default' | 'warning' | 'success' | 'error' } => {
  switch (s) {
    case 'submitted':
      return { label: 'Submitted', color: 'warning' };
    case 'reviewing':
      return { label: 'Reviewing', color: 'warning' };
    case 'resolved':
      return { label: 'Resolved', color: 'success' };
    case 'rejected':
      return { label: 'Rejected', color: 'error' };
    default:
      return { label: s, color: 'default' };
  }
};

const displayUser = (u?: UserMini | null): string => {
  if (!u) return '';
  const alias = (u.alias || '').trim();
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return alias || name || (u.email || '') || u.id;
};

const formatDateTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const ProductReportsTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<ProductReportAdminRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const [query, setQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | ReportStatus>('all');
  const [reasonFilter, setReasonFilter] = React.useState<'all' | ReportReason>('all');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: qErr } = await supabase
        .from('product_reports')
        .select(
          `
          id,
          reporter_id,
          seller_id,
          product_id,
          subject,
          reason,
          details,
          status,
          created_at,
          updated_at,
          reporter:users!product_reports_reporter_id_fkey(id,email,first_name,last_name,alias),
          seller:users!product_reports_seller_id_fkey(id,email,first_name,last_name,alias),
          product:products!product_reports_product_id_fkey(id,name)
        `
        )
        .order('created_at', { ascending: false });

      if (qErr) throw qErr;
      setRows((data || []) as any);
    } catch (e: any) {
      const msg = e?.message || 'Failed to load reports';
      setError(msg);
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const updateStatus = async (id: string, nextStatus: ReportStatus) => {
    const prev = rows;
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status: nextStatus } : x)));

    try {
      const { error: uErr } = await supabase.from('product_reports').update({ status: nextStatus }).eq('id', id);
      if (uErr) throw uErr;
      toast.success('Report updated');
      void load();
    } catch (e: any) {
      setRows(prev);
      toast.error(e?.message || 'Failed to update report');
    }
  };

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();

    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (reasonFilter !== 'all' && r.reason !== reasonFilter) return false;
      if (!q) return true;

      const hay = [
        r.id,
        r.subject,
        r.details,
        r.reason,
        r.status,
        r.product_id,
        r.product?.name || '',
        displayUser(r.reporter),
        displayUser(r.seller),
      ]
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, query, statusFilter, reasonFilter]);

  const countByStatus = React.useMemo(() => {
    const base: Record<ReportStatus, number> = {
      submitted: 0,
      reviewing: 0,
      resolved: 0,
      rejected: 0,
    };
    for (const r of rows) {
      const s = r.status as ReportStatus;
      if (base[s] !== undefined) base[s] += 1;
    }
    return base;
  }, [rows]);

  return (
    <Box>
      <Card sx={{ boxShadow: '0 14px 40px rgba(15, 23, 42, 0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={950} sx={{ fontSize: 18, lineHeight: 1.1 }}>
                Product reports
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Review and manage user-submitted listing reports.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={() => load()} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => load()}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'grey.50',
              mb: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <TextField
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search subject, details, user, product…"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="reports-status">Status</InputLabel>
                  <Select
                    labelId="reports-status"
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="submitted">Submitted ({countByStatus.submitted})</MenuItem>
                    <MenuItem value="reviewing">Reviewing ({countByStatus.reviewing})</MenuItem>
                    <MenuItem value="resolved">Resolved ({countByStatus.resolved})</MenuItem>
                    <MenuItem value="rejected">Rejected ({countByStatus.rejected})</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="reports-reason">Reason</InputLabel>
                  <Select
                    labelId="reports-reason"
                    label="Reason"
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="prohibited_item">Prohibited item</MenuItem>
                    <MenuItem value="counterfeit">Counterfeit / replica</MenuItem>
                    <MenuItem value="misleading_description">Misleading description</MenuItem>
                    <MenuItem value="fraud_scam">Fraud / scam</MenuItem>
                    <MenuItem value="stolen_item">Stolen item</MenuItem>
                    <MenuItem value="hate_harassment">Hate / harassment</MenuItem>
                    <MenuItem value="adult_content">Adult content</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Chip icon={<ReportProblemIcon />} label={`${filtered.length} shown`} sx={{ fontWeight: 800 }} />
                <Chip icon={<ScheduleIcon />} label={`Total ${rows.length}`} variant="outlined" sx={{ fontWeight: 800 }} />
              </Stack>
            </Stack>
          </Paper>

          {loading && rows.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 2.5, color: 'text.secondary' }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading reports…</Typography>
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ p: 2.5 }}>
              <Typography fontWeight={900}>Could not load reports</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && filtered.length === 0 && (
            <Box sx={{ p: 2.5 }}>
              <Typography fontWeight={900}>No reports found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Try adjusting filters or search.
              </Typography>
            </Box>
          )}

          <Stack spacing={1.25}>
            {filtered.map((r) => {
              const s = statusMeta(r.status);
              return (
                <Accordion
                  key={r.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', width: '100%' }}>
                      <Chip label={s.label} color={s.color} size="small" sx={{ fontWeight: 900 }} />
                      <Chip label={reasonLabel(r.reason)} size="small" variant="outlined" sx={{ fontWeight: 800 }} />
                      <Typography sx={{ fontWeight: 950, flex: 1, minWidth: 220 }} noWrap>
                        {r.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {formatDateTime(r.created_at)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
                    <Stack spacing={1.25}>
                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
                          <Chip icon={<PersonIcon />} label={`Reporter: ${displayUser(r.reporter) || r.reporter_id}`} sx={{ fontWeight: 800 }} />
                          <Chip icon={<PersonIcon />} variant="outlined" label={`Seller: ${displayUser(r.seller) || r.seller_id}`} sx={{ fontWeight: 800 }} />
                          <Chip
                            icon={<ShoppingBagIcon />}
                            variant="outlined"
                            label={`Product: ${(r.product?.name || '').trim() || r.product_id}`}
                            sx={{ fontWeight: 800 }}
                          />
                        </Stack>
                      </Paper>

                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 950, mb: 0.75 }}>
                          Details
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {r.details}
                        </Typography>
                      </Paper>

                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id={`status-${r.id}`}>Status</InputLabel>
                            <Select
                              labelId={`status-${r.id}`}
                              label="Status"
                              value={r.status}
                              onChange={(e) => updateStatus(r.id, e.target.value as any)}
                            >
                              <MenuItem value="submitted">Submitted</MenuItem>
                              <MenuItem value="reviewing">Reviewing</MenuItem>
                              <MenuItem value="resolved">Resolved</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                          </FormControl>
                          <Box sx={{ flex: 1 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Updated: {formatDateTime(r.updated_at)}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductReportsTab;
